/**
 * Ειδοποιητήρια — the renewal-notice call sheet.
 *
 * This screen replaces Εισπράξεις in the navigation, and the reason is a correction rather
 * than a redesign. The market review that shaped this app listed «ειδοποιητήρια / δόσεις» as
 * a single gap and the build delivered only the instalments half — a per-instalment
 * receivables ledger. It turned out to model work that does not exist in Christina's office:
 * premium instalments there are a client-to-bank arrangement she never handles, so she never
 * chases an instalment. What she does chase is the phone call.
 *
 * The task this serves: the carrier issues a notice before expiry, the agent rings the client
 * about ten days out — «λήγει σε Χ μέρες, ανανεώνουμε;» — and records what was said. Until now
 * that step existed only in the agent's head, which is why the renewals board could say what
 * was expiring but never what had been *done* about it.
 *
 * Rows are policies, not notices, because there is exactly one live notice per policy period
 * and inventing a second entity for it would buy nothing but a join.
 */

import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
/*
 * `CheckCheck`, not a second phone glyph. The first attempt paired `Phone` with
 * `PhoneOutgoing` — two handsets differing by a 6px arrow, side by side in a 60px column,
 * which is no distinction at all at icon-button size. A double tick is the read-receipt
 * gesture and reads as "this client has been reached"; it is also deliberately not the
 * `CheckCircle2` that means «Εξόφληση», so the money action and the call action cannot be
 * confused for one another.
 */
import { CheckCheck, Mail, MailCheck, Phone, Send } from 'lucide-react';
import {
  addDays,
  formatPolicyRef,
  today as isoToday,
  nonRenewalHorizon,
  urgencyOf,
  type PolicyDto,
} from '@ias/shared';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input, Textarea } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton, Tooltip } from '@/components/ui/misc';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DataTable, type DataColumn } from '@/components/data-table/data-table';
import { EnumBadge, useEnumOptions } from '@/components/common/enum-badge';
import { PageHeader } from '@/components/common/page-header';
import { PolicyRef } from '@/components/common/policy-ref';
import { FilterBar, type FilterDescriptor } from '@/components/filters/filter-bar';
import { RequiresConnection } from '@/components/offline/offline-indicator';
import { ApiRequestError } from '@/lib/api/client';
import {
  policiesApi,
  useInsuranceCompanies,
  useRenewalEmailPreview,
  useRenewalEmailSettings,
  useSendDueRenewalEmails,
  useSendRenewalEmail,
  useSendRenewalEmails,
  useUpdateRenewalNotice,
} from '@/lib/api/domains';
import { useAuth } from '@/lib/auth';
import { useI18n } from '@/lib/i18n';
import { useListState } from '@/hooks/use-list-state';
import { DaysBadge } from './policies/list';

/**
 * How far ahead the sheet looks.
 *
 * Wider than the renewals board's 30 days on purpose: carriers issue notices early, and a
 * notice that arrives 40 days out is work the moment it lands. Cancelled policies are excluded
 * by the status filter rather than by a separate flag — a cancelled policy has no renewal to
 * discuss, and leaving it in would put a call on the sheet that must not be made.
 */
const WINDOW_DAYS = 45;

export function NoticesPage() {
  const { t, dateTime, date } = useI18n();
  const navigate = useNavigate();
  const { can } = useAuth();

  const list = useListState({
    defaultSortBy: 'endDate',
    defaultSortOrder: 'asc',
    fixed: {
      endDateTo: addDays(isoToday(), WINDOW_DAYS),
      /*
       * And a floor, added once the archive import landed 4.733 policies whose last period
       * closed years ago — one of them in 2010. `expired` belongs in the status filter for the
       * policy that lapsed last week; it put the whole archive on the call sheet, every row
       * wearing a red «Εκκρεμεί» for a notice nobody will ever send. The horizon is the same
       * rule the list colours by: past it, there is no renewal conversation to have.
       */
      endDateFrom: nonRenewalHorizon(),
      status: 'active,renewal_due,expired',
    },
  });
  /*
   * «Δεν ανανεώνει» closes the conversation this sheet exists to have, so the row leaves —
   * the same rule the renewals board already applied, which is why a declined policy vanished
   * from one screen and stayed on the other. Recording the answer had no visible effect here,
   * so the call looked un-made and got made twice.
   *
   * Not fixed into the query: the toggle below brings them back, because a declined renewal is
   * worth revisiting and the notice dialog says as much.
   */
  const { data, isLoading, isFetching, error, refetch } = policiesApi.useList({
    ...list.params,
    ...(list.filters.showDeclined ? {} : { hideDeclinedNotices: 'true' }),
  });
  const { data: insurers } = useInsuranceCompanies();
  const { data: emailSettings } = useRenewalEmailSettings();
  const [calling, setCalling] = React.useState<PolicyDto | null>(null);
  const [emailing, setEmailing] = React.useState<PolicyDto | null>(null);
  const [selected, setSelected] = React.useState<string[]>([]);

  const sendMany = useSendRenewalEmails();
  const sendDue = useSendDueRenewalEmails();

  const pending = (data?.data ?? []).filter((policy) => policy.noticeStatus === 'pending').length;

  /*
   * Whether this screen offers the email channel at all.
   *
   * Off is not the same as broken: an agency that has not turned renewal emails on should see a
   * sheet about phone calls, exactly as before, rather than a column of disabled buttons
   * explaining a feature it has not asked for. The mail affordances therefore appear only once
   * the setting is on — and the *reason* a particular row cannot be emailed (no address on the
   * client) is shown on that row, because that one is fixable from here.
   */
  const emailEnabled = Boolean(emailSettings?.enabled) && can('policy:write');

  const runBatch = async (run: Promise<{ sent: number; skipped: number }>) => {
    try {
      const result = await run;
      const summary = t('notices.emailBatchResult', { sent: result.sent, skipped: result.skipped });
      if (result.sent > 0) toast.success(summary);
      else toast.warning(summary);
      setSelected([]);
    } catch (caught) {
      toast.error(caught instanceof ApiRequestError ? caught.message : t('error.generic'));
    }
  };

  const columns = React.useMemo<DataColumn<PolicyDto>[]>(
    () => [
      {
        id: 'clientName',
        header: t('client.one'),
        meta: { headerLabel: t('client.one') },
        cell: ({ row }) => (
          <div className="min-w-0">
            <p className="truncate font-medium">{row.original.clientName}</p>
            <PolicyRef
              policyNumber={row.original.policyNumber}
              plates={row.original.plates}
              className="text-muted-foreground text-xs"
              muted
            />
          </div>
        ),
      },
      {
        id: 'insuranceCompanyName',
        header: t('policy.company'),
        accessorKey: 'insuranceCompanyName',
        meta: { hideBelow: 'lg', headerLabel: t('policy.company') },
      },
      {
        id: 'insuranceType',
        header: t('policy.insuranceType'),
        meta: { hideBelow: 'xl', headerLabel: t('policy.insuranceType') },
        cell: ({ row }) => <EnumBadge enum="insuranceType" value={row.original.insuranceType} size="sm" />,
      },
      {
        id: 'endDate',
        header: t('policy.endDate'),
        meta: { sortKey: 'endDate', numeric: true, headerLabel: t('policy.endDate') },
        cell: ({ row }) => (
          <span className="flex items-center gap-2 tabular">
            {date(row.original.endDate)}
            <DaysBadge days={row.original.daysUntilExpiry} />
          </span>
        ),
      },
      {
        id: 'noticeStatus',
        header: t('notices.status'),
        meta: { headerLabel: t('notices.status') },
        cell: ({ row }) => (
          <EnumBadge enum="renewalNoticeStatus" value={row.original.noticeStatus} size="sm" />
        ),
      },
      {
        id: 'notifiedAt',
        header: t('notices.notifiedAt'),
        meta: { hideBelow: 'lg', headerLabel: t('notices.notifiedAt') },
        cell: ({ row }) =>
          row.original.notifiedAt ? (
            <span className="text-muted-foreground text-xs tabular">{dateTime(row.original.notifiedAt)}</span>
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      },
      /*
       * The email column exists only while the channel does. It states the fact rather than
       * repeating the send button that already sits in the actions cell: what an agent scanning
       * the sheet needs is "has this one been written to", and the answer is a date or nothing.
       */
      ...(emailEnabled
        ? [
            {
              id: 'noticeEmail',
              header: t('notices.email'),
              meta: { hideBelow: 'xl', headerLabel: t('notices.email') },
              cell: ({ row }: { row: { original: PolicyDto } }) =>
                row.original.noticeEmailSentAt ? (
                  <Tooltip content={row.original.noticeEmailSentTo ?? ''}>
                    <Badge tone="good" size="sm">
                      <MailCheck className="size-3" />
                      {date(row.original.noticeEmailSentAt)}
                    </Badge>
                  </Tooltip>
                ) : row.original.clientEmail ? (
                  <span className="text-muted-soft text-xs">{t('notices.emailNotSent')}</span>
                ) : (
                  <span className="text-muted-soft text-xs">{t('notices.emailNoAddress')}</span>
                ),
            } satisfies DataColumn<PolicyDto>,
          ]
        : []),
      {
        id: 'actions',
        header: '',
        meta: { align: 'right' },
        cell: ({ row }) => (
          <div className="flex items-center justify-end gap-0.5" onClick={(e) => e.stopPropagation()}>
            {row.original.clientPhone && (
              <Button variant="ghost" size="icon-sm" asChild aria-label={t('action.call')}>
                <a href={`tel:${row.original.clientPhone}`}>
                  <Phone />
                </a>
              </Button>
            )}
            {/*
              Disabled rather than hidden when the client has no address: an absent button reads
              as "this screen cannot do that", while a disabled one with the reason on hover
              reads as "this client is missing something" — which is the true and fixable case.
            */}
            {emailEnabled && (
              <Tooltip
                content={
                  !row.original.clientEmail
                    ? t('notices.emailNoAddress')
                    : row.original.noticeEmailSentAt
                      ? t('notices.emailResend')
                      : t('notices.emailSend')
                }
              >
                <span>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    disabled={!row.original.clientEmail}
                    onClick={() => setEmailing(row.original)}
                    aria-label={t('notices.emailSend')}
                  >
                    <Mail className={row.original.noticeEmailSentAt ? 'text-good' : undefined} />
                  </Button>
                </span>
              </Tooltip>
            )}
            {can('policy:write') && (
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={() => setCalling(row.original)}
                aria-label={t('notices.record')}
              >
                <CheckCheck />
              </Button>
            )}
          </div>
        ),
      },
    ],
    [t, date, dateTime, can, emailEnabled],
  );

  const filters = React.useMemo<FilterDescriptor[]>(
    () => [
      {
        key: 'noticeStatus',
        type: 'enum',
        enum: 'renewalNoticeStatus',
        label: t('notices.status'),
        multiple: true,
        column: 'noticeStatus',
      },
      {
        key: 'insuranceCompanyId',
        type: 'options',
        label: t('policy.company'),
        column: 'insuranceCompanyName',
        options: (insurers ?? []).map((insurer) => ({ value: insurer.id, label: insurer.name })),
      },
      {
        key: 'insuranceType',
        type: 'enum',
        enum: 'insuranceType',
        label: t('policy.insuranceType'),
        multiple: true,
        column: 'insuranceType',
      },
      { key: 'endDateFrom', type: 'date', label: t('policy.endDate'), column: 'endDate' },
      // Brings back the rows whose client already said no — nothing is ever hidden from an
      // explicit request, only from the untouched call sheet.
      { key: 'showDeclined', type: 'switch', label: t('notices.showDeclined') },
    ],
    [t, insurers],
  );

  return (
    <>
      <PageHeader
        title={t('notices.title')}
        description={t('notices.subtitle')}
        actions={
          <div className="flex flex-wrap items-center gap-3">
            {pending > 0 && (
              <span className="text-muted-foreground text-[13px]">
                <span className="text-foreground font-semibold tabular">{pending}</span>{' '}
                {t('notices.pendingCount')}
              </span>
            )}
            {/*
              The administrator's manual trigger. Offered only in `manual` mode — in `auto` the
              same window is swept every morning, so a button that re-sends it by hand is at best
              a no-op and at worst a way to double-send after someone changes the lead time. What
              `auto` gets instead is a line saying so, because "why is there no button here" is
              otherwise answered only by visiting Settings.
            */}
            {emailEnabled && can('user:manage') && emailSettings?.mode === 'manual' && (
              <Tooltip content={t('notices.emailSendDueHint')}>
                <Button
                  variant="outline"
                  size="sm"
                  loading={sendDue.isPending}
                  onClick={() => void runBatch(sendDue.mutateAsync(undefined))}
                >
                  <Send /> {t('notices.emailSendDue')}
                </Button>
              </Tooltip>
            )}
            {emailEnabled && emailSettings?.mode === 'auto' && (
              <span className="text-muted-soft text-[12.5px]">
                {t('notices.emailAutoNotice', { days: emailSettings.leadDays })}
              </span>
            )}
          </div>
        }
      />

      <FilterBar
        className="mb-5"
        listState={list}
        inline="unbound"
        searchPlaceholder={`${t('policy.number')} / ${t('client.one')}`}
        filters={filters}
      />

      <DataTable
        columns={columns}
        rows={data?.data}
        total={data?.meta.total}
        listState={list}
        filters={filters}
        isLoading={isLoading}
        isFetching={isFetching}
        error={error}
        onRetry={refetch}
        getRowId={(policy) => policy.id}
        onRowClick={(policy) => navigate(`/policies/${policy.id}`)}
        /*
         * Selection appears with the email channel. It has no other use on this sheet — a phone
         * call cannot be made in bulk — so offering checkboxes while the channel is off would be
         * a column of controls with nothing to do.
         */
        selection={
          emailEnabled
            ? {
                selectedIds: selected,
                onChange: setSelected,
                actions: (
                  <Button
                    size="sm"
                    loading={sendMany.isPending}
                    onClick={() => void runBatch(sendMany.mutateAsync(selected))}
                  >
                    <Send /> {t('notices.emailSendSelected')}
                  </Button>
                ),
              }
            : undefined
        }
        rowClassName={(policy) =>
          policy.noticeStatus === 'pending' && urgencyOf(policy.endDate) === 'critical'
            ? 'row-tint-critical'
            : undefined
        }
        emptyTitle={t('notices.empty')}
        emptyHint={t('notices.hint')}
      />

      {calling && (
        <NoticeDialog policy={calling} open onOpenChange={(next) => !next && setCalling(null)} />
      )}

      {emailing && (
        <RenewalEmailDialog
          policy={emailing}
          open
          onOpenChange={(next) => !next && setEmailing(null)}
        />
      )}
    </>
  );
}

/**
 * Records the call.
 *
 * The status is the whole point and so it is the first field, pre-selected to `notified` — the
 * dialog is opened by someone who has just rung, so the common case should need one click.
 * The note is free text because «τι είπε ο πελάτης» does not fit an enum, and it is what makes
 * a `declined` worth revisiting later.
 */
export function NoticeDialog({
  policy,
  open,
  onOpenChange,
}: {
  policy: PolicyDto;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useI18n();
  const statusOptions = useEnumOptions('renewalNoticeStatus');
  const update = useUpdateRenewalNotice();

  const [noticeStatus, setNoticeStatus] = React.useState<string>(
    policy.noticeStatus === 'pending' ? 'notified' : policy.noticeStatus,
  );
  const [noticeReceivedAt, setNoticeReceivedAt] = React.useState(policy.noticeReceivedAt ?? '');
  const [noticeNote, setNoticeNote] = React.useState(policy.noticeNote ?? '');

  const submit = async () => {
    try {
      await update.mutateAsync({
        policyId: policy.id,
        noticeStatus,
        noticeReceivedAt: noticeReceivedAt || null,
        noticeNote: noticeNote.trim() || null,
      });
      toast.success(formatPolicyRef(policy.policyNumber, policy.plates));
      onOpenChange(false);
    } catch (error) {
      toast.error(error instanceof ApiRequestError ? error.message : t('error.generic'));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>{t('notices.record')}</DialogTitle>
          <DialogDescription>
            {formatPolicyRef(policy.policyNumber, policy.plates)} · {policy.clientName}
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="noticeStatus">{t('notices.status')}</Label>
            <Select value={noticeStatus} onValueChange={setNoticeStatus}>
              <SelectTrigger id="noticeStatus">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {statusOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="noticeReceivedAt">{t('notices.received')}</Label>
            <Input
              id="noticeReceivedAt"
              type="date"
              value={noticeReceivedAt}
              onChange={(event) => setNoticeReceivedAt(event.target.value)}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="noticeNote">{t('notices.note')}</Label>
            <Input
              id="noticeNote"
              value={noticeNote}
              onChange={(event) => setNoticeNote(event.target.value)}
            />
          </div>
        </DialogBody>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('action.cancel')}
          </Button>
          <RequiresConnection reasonKey="offline.blocked.contract">
            <Button onClick={() => void submit()} loading={update.isPending}>
              {t('action.save')}
            </Button>
          </RequiresConnection>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Sending the ειδοποιητήριο by email.
 *
 * The dialog shows what will actually be sent, rendered by the server with this policy's own
 * data, before it is sent. That is the point of it existing rather than the row's button firing
 * straight away: a template is written once and read by every client afterwards, and the cheapest
 * moment to notice that {{premium}} came out as «0,00 €» is while the message is still on screen.
 *
 * The copy is read-only here. Editing one client's notice would make the record ambiguous — the
 * database stores that a notice went out, not which words went out — and the place to change the
 * words is the template.
 */
function RenewalEmailDialog({
  policy,
  open,
  onOpenChange,
}: {
  policy: PolicyDto;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t, dateTime } = useI18n();
  const { data: preview, isLoading, error } = useRenewalEmailPreview(policy.id);
  const send = useSendRenewalEmail();

  const submit = async () => {
    try {
      const result = await send.mutateAsync(policy.id);
      if (result.sent) {
        toast.success(`${formatPolicyRef(policy.policyNumber, policy.plates)} → ${result.recipient}`);
        onOpenChange(false);
      } else {
        toast.error(result.reason ?? t('error.generic'));
      }
    } catch (caught) {
      toast.error(caught instanceof ApiRequestError ? caught.message : t('error.generic'));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>{t('notices.emailSend')}</DialogTitle>
          <DialogDescription>
            {formatPolicyRef(policy.policyNumber, policy.plates)} · {policy.clientName}
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-4">
          {isLoading ? (
            <>
              <Skeleton className="h-9 w-full" />
              <Skeleton className="h-9 w-2/3" />
              <Skeleton className="h-48 w-full" />
            </>
          ) : error || !preview ? (
            <p role="alert" className="text-destructive text-[13px]">
              {error?.message ?? t('error.generic')}
            </p>
          ) : (
            <>
              {preview.blocker && (
                <p
                  role="alert"
                  className="border-critical/40 bg-critical/[0.07] text-foreground rounded-lg border px-3 py-2 text-[13px]"
                >
                  {preview.blocker}
                </p>
              )}

              {/* A resend is legitimate and deliberately not blocked — but it must not be
                  accidental, so the previous send is stated above the button that repeats it. */}
              {preview.alreadySentAt && (
                <p className="text-muted-foreground text-[13px]">
                  {t('notices.emailSentAt', { date: dateTime(preview.alreadySentAt) })}
                </p>
              )}

              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label>{t('notices.emailRecipient')}</Label>
                  <Input value={preview.recipient ?? '—'} readOnly />
                </div>
                <div className="space-y-1.5">
                  <Label>{t('notices.emailSubject')}</Label>
                  <Input value={preview.subject} readOnly />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label>{t('notices.emailPreview')}</Label>
                <Textarea value={preview.body} readOnly rows={14} className="text-[13px]" />
              </div>
            </>
          )}
        </DialogBody>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('action.cancel')}
          </Button>
          <RequiresConnection reasonKey="offline.blocked.contract">
            <Button
              onClick={() => void submit()}
              loading={send.isPending}
              disabled={!preview || Boolean(preview.blocker)}
            >
              <Send />
              {preview?.alreadySentAt ? t('notices.emailResend') : t('notices.emailSendShort')}
            </Button>
          </RequiresConnection>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
